import React, { Component } from 'react';
import { Play, Shield, Eye, Clock, Users, CheckCircle, Star, ArrowRight, Phone, Mail, MapPin } from 'lucide-react';

class Access extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeFeature: 0,
      stats: [
    { number: '24/7', label: 'Access Oversight' },
    { number: '99.9%', label: 'System Uptime' },
    { number: '1,000+', label: 'Doors Secured' },
    { number: '<5s', label: 'Access Response Time' }
  ],
  features: [
    {
      icon: <Eye className="w-6 h-6" />,
      title: 'Real-Time Access Monitoring',
      description: 'Manage and monitor access attempts and entry logs in real-time from a central location.'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'AI-Driven Access Intelligence',
      description: 'Leverage AI to detect unauthorized entry attempts and flag suspicious patterns.'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: '24/7 Remote Access Support',
      description: 'Support staff and tenants with around-the-clock remote access management.'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Multi-Site Access Control',
      description: 'Manage access permissions across multiple buildings or zones with ease.'
    }
  ],
  services: [
    {
      title: 'Remote Door Unlocking',
      description: 'Grant or deny access instantly through live operator support.',
      features: ['Video-based verification', 'Visitor authentication', 'Credential override']
    },
    {
      title: 'Smart Credential Management',
      description: 'Issue, manage, and revoke credentials remotely.',
      features: ['Mobile credential provisioning', 'Access schedules', 'Temporary guest passes']
    },
    {
      title: 'Mobile Access Tools',
      description: 'Real-time access control in the palm of your hand.',
      features: ['Remote lock/unlock', 'Entry alerts', 'Digital key access']
    },
    {
      title: 'System Integration',
      description: 'Unify access control with HR, CRM, or BMS platforms.',
      features: ['SSO access compatibility', 'Audit trail generation', 'Intercom & gate integrations']
    }
  ],
  testimonials: [
    {
      name: 'Sarah Johnson',
      company: 'Metro Logistics',
      rating: 5,
      text: 'We can now manage multiple facility entries from one dashboard. It’s a game changer!'
    },
    {
      name: 'Michael Chen',
      company: 'Warehouse 360',
      rating: 5,
      text: 'Highly secure and easy to use. Access issues are resolved instantly with remote unlock support.'
    },
    {
      name: 'Lisa Rodriguez',
      company: 'Green Valley Apartments',
      rating: 5,
      text: 'No more lost keys or unauthorized entries. Their mobile access and AI alerts are excellent.'
    }
  ]
    };
  }

  componentDidMount() {
    // Auto-rotate features every 4 seconds
    this.featureInterval = setInterval(() => {
      this.setState(prevState => ({
        activeFeature: (prevState.activeFeature + 1) % prevState.features.length
      }));
    }, 4000);
  }

  componentWillUnmount() {
    if (this.featureInterval) {
      clearInterval(this.featureInterval);
    }
  }

  setActiveFeature = (index) => {
    this.setState({ activeFeature: index });
  }

  render() {
    const { activeFeature, stats, features, services, testimonials } = this.state;

    const styles = {
      root: {
        '--primary-dark': '#010A1A',
        '--secondary-dark': '#002C58',
        '--text-primary': '#ffffff',
        '--text-secondary': '#94a3b8',
        '--box-dark-bg': '#1a2332',
        '--icons-color': '#155EEF',
        '--accent-cyan': '#06b6d4',
        '--accent-blue': '#3b82f6'
      }
    };

    return (
      <div className="min-vh-100" 
           style={{
             backgroundColor: 'var(--primary-dark)',
             color: 'var(--text-primary)',
             fontFamily: 'system-ui, -apple-system, sans-serif'
           }}>
        
        {/* Banner Section */}
        <section className="general-banner">
          <div className="container">
            <div className="general-content">
              <h1 className="hero-title orbitron">Remote Access Management</h1>
              <p className="hero-subtitle">Secure and seamless control over facility access with real-time monitoring and AI automation</p>
            </div>
          </div>
        </section>


        {/* Features Section */}
        <section className="py-5">
          <div className="container">
            <div className="row mb-5">
              <div className="col-12 text-center">
                <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Advanced Security Features
                </h2>
                <p className="lead" style={{ color: 'var(--text-secondary)' }}>
                  Cutting-edge technology meets professional expertise
                </p>
              </div>
            </div>
            <div className="row g-4">
              {features.map((feature, index) => (
                <div key={index} className="col-lg-6 col-md-6">
                  <div className={`p-4 h-100 rounded-3 cursor-pointer transition-all ${
                         activeFeature === index ? 'border-2' : 'border-1'
                       }`}
                       style={{
                         backgroundColor: activeFeature === index ? 'var(--secondary-dark)' : 'var(--box-dark-bg)',
                         borderColor: activeFeature === index ? 'var(--accent-blue)' : 'var(--secondary-dark)',
                         transition: 'all 0.3s ease',
                         cursor: 'pointer'
                       }}
                       onClick={() => this.setActiveFeature(index)}>
                    <div className="d-flex align-items-start gap-3">
                      <div className="p-3 rounded-2 flex-shrink-0" 
                           style={{ 
                             backgroundColor: 'var(--icons-color)', 
                             color: 'white' 
                           }}>
                        {feature.icon}
                      </div>
                      <div>
                        <h4 className="fw-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                          {feature.title}
                        </h4>
                        <p className="mb-0" style={{ color: 'var(--text-secondary)' }}>
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-5" style={{ backgroundColor: 'var(--box-dark-bg)' }}>
          <div className="container">
            <div className="row mb-5">
              <div className="col-12 text-center">
                <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Our Monitoring Services
                </h2>
                <p className="lead" style={{ color: 'var(--text-secondary)' }}>
                  Comprehensive security solutions tailored to your needs
                </p>
              </div>
            </div>
            <div className="row g-4">
              {services.map((service, index) => (
                <div key={index} className="col-lg-6">
                  <div className="p-4 h-100 rounded-3" 
                       style={{
                         backgroundColor: 'var(--primary-dark)',
                         border: '1px solid var(--secondary-dark)'
                       }}>
                    <h4 className="fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                      {service.title}
                    </h4>
                    <p className="mb-3" style={{ color: 'var(--text-secondary)' }}>
                      {service.description}
                    </p>
                    <ul className="list-unstyled">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="d-flex align-items-center gap-2 mb-2">
                          <CheckCircle className="w-4 h-4 flex-shrink-0" 
                                      style={{ color: 'var(--accent-cyan)' }} />
                          <span style={{ color: 'var(--text-secondary)' }}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      

        {/* CTA Section */}
        <section className="py-5" 
                 style={{
                   background: `linear-gradient(135deg, var(--secondary-dark) 0%, var(--accent-blue) 100%)`
                 }}>
          <div className="container">
            <div className="row">
              <div className="col-12 text-center">
                <h2 className="display-5 fw-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Ready to Secure Your Property?
                </h2>
                <p className="lead mb-4" style={{ color: 'var(--text-primary)' }}>
                  Get started with our professional live video monitoring services today
                </p>
                <div className="d-flex flex-wrap justify-content-center gap-3">
                  <button className="btn btn-light btn-lg px-4 py-2 d-flex align-items-center gap-2">
                    <Phone className="w-4 h-4" /> Call Now: +1 (403) 992-1672
                  </button>
                  {/* <button className="btn btn-outline-light btn-lg px-4 py-2 d-flex align-items-center gap-2">
                    <Mail className="w-4 h-4" /> Get Free Quote
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </section>

       
      </div>
    );
  }
}

export default Access;